import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import HorizontalTitleList from '@/components/timeline/HorizontalTitleList.vue';

import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { toastError } from '@/helpers/vfm/toast-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers';

import { useRoute } from 'vue-router/composables';
import { useFilterStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery } from '@/helpers/composables/useApollo';

import type { GetNewTitlesQuery, GetNewTitlesQueryVariables } from '@/pages/graphql/queries/GetNewTitles.query';
import { GetNewTitlesDocument } from '@/pages/graphql/queries/GetNewTitles.query';

import { NewDateRangeBucket, NewPageType, ObjectType, Platform, TitleFilter } from '@/@types/graphql-types';
import { CollectionType } from '@/enums/collection-type';
import { useMounted } from '@vueuse/core';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProviderTimeline',
  props: {
    providerId: null,
    timelineDate: null,
    providerIcon: null,
    providerShortName: { default: '' },
    dayIndex: null,
    titleCount: null,
    providerIndex: null,
    bucketType: null,
    additionalContexts: { default: () => [] }
  },
  emits: ['onPosterClick', 'passUpTitles'],
  setup(__props: any, { emit }) {

const props = __props as { providerId: number, timelineDate: string, providerIcon: string, providerShortName: string, dayIndex?: number | null, titleCount?: number | null, providerIndex?: number | null, bucketType?: NewDateRangeBucket | null, additionalContexts: SnowplowContext[] };





const route = useRoute();
const { t, tc } = useI18n();
const { language, country } = useLanguageStore();
const { filters, selectedProviders } = useFilterStore();
const { isHideSeenDislikedTitlesEnabled, isLoggedIn } = useUserStore();

const isMounted = useMounted();

const isFirstPageLoaded = ref(false);

const newPageType = computed(() => route.meta?.newPageType as NewPageType);
const isPriceDropsTimeline = computed(() => newPageType.value === NewPageType.PriceDrop);

const filtersNew = computed(() => filters.value(CollectionType.NEW));
const graphqlFilters = computed<TitleFilter>(() =>
	toGraphqlFilters(
		filtersNew.value,
		selectedProviders.value(!isLoggedIn.value),
		isHideSeenDislikedTitlesEnabled.value
	)
);

// We need this mapper due to filter naming on the New Page
// We have a TV Show filter on New Page that needs to fetch only TV Show Seasons items
const filterObjectTypes = computed(() =>
	graphqlFilters.value?.objectTypes?.map(objectType =>
		objectType === ObjectType.Show ? ObjectType.ShowSeason : objectType
	)
);

const queryVariables = computed<GetNewTitlesQueryVariables>(() => {
	const variables: GetNewTitlesQueryVariables = {
		date: props.timelineDate,
		filter: {
			...graphqlFilters.value,
			objectTypes: filterObjectTypes.value,
			packages: [props.providerShortName],
		},
		language: language.value,
		country: country.value,
		priceDrops: isPriceDropsTimeline.value,
		platform: Platform.Web,
		pageType: newPageType.value,
		showDateBadge:
			!!props.bucketType &&
			[NewDateRangeBucket.Next_7Days, NewDateRangeBucket.Next_30Days].includes(props.bucketType),
		availableToPackages: [props.providerShortName],
	};

	if ([NewPageType.LeavingSoon, NewPageType.Upcoming].includes(newPageType.value) && props.bucketType) {
		variables.bucketType = props.bucketType;
	}

	return variables;
});

const { result, loading, onResult, onError, fetchMore } = useQuery<GetNewTitlesQuery, GetNewTitlesQueryVariables>(
	GetNewTitlesDocument,
	queryVariables,
	() => ({
		fetchPolicy: 'cache-and-network',
		enabled: !!newPageType.value,
	})
);

onResult(() => {
	isFirstPageLoaded.value = true;
});

onError(() => {
	if (process.client) {
		toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
	}
});

const providerTitlesData = computed(() => result.value?.newTitles);
const lazyLoadImages = computed(
	() => !!props.dayIndex || (props.providerIndex && props.providerIndex >= global.IMG_NEW_TIMELINE_ROWS)
);
const imagesLoadCount = computed(() =>
	!props.dayIndex && props.providerIndex && props.providerIndex < global.IMG_NEW_TIMELINE_ROWS
		? global.IMG_POSTER_LOAD_COUNT
		: 0
);

const providerTitles = computed(() =>
	(providerTitlesData.value?.edges ?? []).map(e => ({ ...e.node, watchNowOffer: e.watchNowOffer }))
);

const titleCountLabel = computed(() =>
	props.titleCount === 1
		? t('WEBAPP_TITLE', { NUMBER: props.titleCount })
		: t('WEBAPP_TITLES', { NUMBER: props.titleCount })
);

watch(filtersNew, () => {
	isFirstPageLoaded.value = false;
});

watch(providerTitlesData, () => {
	const edges = providerTitlesData.value?.edges || [];
	if (!!edges.length) {
		emit('passUpTitles', edges);
	}
});

function onPosterClick($event: { index: number; title: any }) {
	emit('onPosterClick', { ...$event, providerId: props.providerId, date: props.timelineDate });
}

async function onLoadNext(complete: Function) {
	if (!complete) return;

	try {
		await fetchMore({
			variables: {
				after: providerTitlesData.value?.pageInfo?.endCursor,
			},
			updateQuery(previousResult, { fetchMoreResult }) {
				if (!fetchMoreResult) return previousResult;

				return {
					newTitles: {
						__typename: previousResult.newTitles.__typename,
						totalCount: fetchMoreResult.newTitles.totalCount,
						edges: [...(previousResult.newTitles.edges || []), ...(fetchMoreResult.newTitles.edges || [])],
						pageInfo: fetchMoreResult.newTitles.pageInfo,
					},
				};
			},
		});
	} catch (error: any) {
		captureMessageForSentry(
			'[GraphQL New Page Load More Bucket Titles Issue]:',
			{ error, where: 'Component: ProviderTimeline, method: onLoadNext' },
			'error'
		);
	} finally {
		complete();
	}
}

return { __sfc: true,props, emit, route, t, tc, language, country, filters, selectedProviders, isHideSeenDislikedTitlesEnabled, isLoggedIn, isMounted, isFirstPageLoaded, newPageType, isPriceDropsTimeline, filtersNew, graphqlFilters, filterObjectTypes, queryVariables, result, loading, onResult, onError, fetchMore, providerTitlesData, lazyLoadImages, imagesLoadCount, providerTitles, titleCountLabel, onPosterClick, onLoadNext, ProviderIcon, PosterGraphql, HorizontalTitleList }
}

})